"use client"

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import useSearchModal from "@/hooks/use-open-search";
import { Category, Post } from "@prisma/client";
import { BookOpenIcon, CogIcon, MailIcon, TagIcon } from "lucide-react";
import Link from "next/link";
import { useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";

export default function SearchPublic({ posts, categories, siteId }: { posts: Post[]; categories: Category[], siteId: string }) {
  const { isOpen, openModal, closeModal, toggleModal } = useSearchModal();

  // Use the hotkey "ctrl + k" to toggle the modal
  useHotkeys("ctrl+k", (event) => {
    event.preventDefault();
    toggleModal();
  });

  // This effect handles the "ctrl + k" keyboard shortcut globally
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleModal();
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(isOpen) => (isOpen ? openModal() : closeModal())}>
      <DialogContent className="p-0 max-w-[640px]">
        <Command className="rounded-lg border shadow-md">
          <CommandInput placeholder="Type to search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <ScrollArea className="h-[300px]">
              <CommandGroup heading="Categories">
                {categories.map((category) => (
                  <CommandItem key={category.id} asChild>
                    <Link className="cursor-pointer" href={`/site/${siteId}/categories`} onClick={closeModal}>
                      <TagIcon className="mr-2 h-4 w-4" />
                      <span>{category.name}</span>
                    </Link>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandGroup heading="Articles">
                {posts.map((post) => (
                  <CommandItem key={post.id} asChild>
                    <Link className="cursor-pointer" href={'/editor/' + post.id} onClick={closeModal}>
                      <BookOpenIcon className="mr-2 h-4 w-4" />
                      <span>{post.title}</span>
                    </Link>
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandGroup heading="Settings">
                <CommandItem asChild>
                  <Link href={`/site/${siteId}/settings`} onClick={closeModal}>
                    <CogIcon className="mr-2 h-4 w-4" />
                    <span>General Settings</span>
                  </Link>
                </CommandItem>
              </CommandGroup>
              <CommandGroup heading="Contact">
                <CommandItem>
                  <MailIcon className="mr-2 h-4 w-4" />
                  <span>Support</span>
                </CommandItem>
                <CommandItem>
                  <MailIcon className="mr-2 h-4 w-4" />
                  <span>Feedback</span>
                </CommandItem>
                <CommandItem>
                  <MailIcon className="mr-2 h-4 w-4" />
                  <span>Report an Issue</span>
                </CommandItem>
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
